import api from "../helpers/axios";

class ChatService {
  /**
   * Get chat messages by chat ID (same as UserViewer)
   * @param {string} chatId - Chat UUID
   * @returns {Promise<Array>} Array of chat messages
   */
  async getChatMessages(chatId) {
    try {
      const response = await api.get(`/admin/chats/${chatId}/messages`);
      return response.data || [];
    } catch (error) {
      console.error("Error fetching chat messages:", error);
      throw error;
    }
  }

  /**
   * Get chat details by chat ID
   * @param {string} chatId - Chat UUID
   * @returns {Promise<Object>} Chat information
   */
  async getChatDetails(chatId) {
    try {
      const response = await api.get(`/admin/chats/${chatId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching chat details:", error);
      throw error;
    }
  }

  /**
   * Get specific message by ID
   * @param {string} messageId - Message UUID
   * @returns {Promise<Object>} Message details
   */
  async getMessageById(messageId) {
    try {
      const response = await api.get(`/admin/messages/${messageId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching message by ID:", error);
      throw error;
    }
  }

  /**
   * Get conversation context around a specific message
   * Uses same simple approach as UserViewer - get all messages and slice for context
   * @param {string} chatId - Chat UUID
   * @param {string} messageId - Message UUID (optional, for highlighting)
   * @param {number} contextSize - Number of recent messages to show (default: 5)
   * @returns {Promise<Object>} Conversation context with messages and chat info
   */
  async getConversationContext(chatId, messageId = null, contextSize = 5) {
    try {
      const allMessages = await this.getChatMessages(chatId);

      // If we have a specific message, try to center context around it
      if (messageId) {
        const messageIndex = allMessages.findIndex((m) => m.id === messageId);
        if (messageIndex !== -1) {
          const startIndex = Math.max(
            0,
            messageIndex - Math.floor(contextSize / 2)
          );
          const endIndex = Math.min(
            allMessages.length,
            startIndex + contextSize
          );
          const contextMessages = allMessages.slice(startIndex, endIndex);

          return {
            messages: contextMessages,
            target_message_id: messageId,
            chat_id: chatId,
            total_messages: allMessages.length,
          };
        }
      }

      // Fallback: get last N messages (like UserViewer)
      const recentMessages = allMessages.slice(-contextSize);
      return {
        messages: recentMessages,
        chat_id: chatId,
        total_messages: allMessages.length,
      };
    } catch (error) {
      console.error("Error fetching conversation context:", error);
      throw error;
    }
  }
}

export default new ChatService();
